import { useState } from "react";
import {
  mutationOptions,
  queryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { AxiosError } from "axios";

import {
  ExportImportStatus,
  IImportData,
  IImportErrors,
  ImportType,
} from "src/Pages/Company/Absences/AbsenceRecords/AbsenceRecords.models";
import __ from "src/utils/Translations";
import { dynamicNotification } from "src/utils/Notifications";
import { fetchImportStatus, importAbsenceData } from "./api";
import { AbsenceQueryKeys } from "./config";

interface ImportAbsenceDataParams {
  companyId: string;
  importType: ImportType;
  file: File;
}

interface ImportResponse {
  importId: string;
}

const ImportAbsenceDataQueries = {
  importStatus: (importId: string | undefined) =>
    queryOptions({
      enabled: !!importId,
      queryKey: AbsenceQueryKeys.importStatus(importId),
      queryFn: (): Promise<IImportData> => fetchImportStatus(importId!),
      refetchInterval: (query) =>
        query.state.data?.status === ExportImportStatus.CREATED ? 500 : false,
    }),

  import: mutationOptions<ImportResponse, AxiosError, ImportAbsenceDataParams>({
    mutationFn: ({ companyId, importType, file }) =>
      importAbsenceData(companyId, importType, file),
  }),
};

export const useImportAbsenceData = () => {
  const queryClient = useQueryClient();
  const [importStatus, setImportStatus] = useState<ExportImportStatus | null>(
    null
  );
  const [errors, setErrors] = useState<IImportErrors | null>(null);

  const importMutation = useMutation({
    ...ImportAbsenceDataQueries.import,
    onSuccess: (data) => {
      queryClient.setQueryData(
        AbsenceQueryKeys.importStatus(data.importId),
        data
      );
    },
    onError: () => {
      dynamicNotification(
        __("Wystąpił błąd podczas importu danych."),
        "error",
        "top-right",
        7000
      );
    },
  });

  const importStatusQuery = useQuery({
    ...ImportAbsenceDataQueries.importStatus(importMutation.data?.importId),
    queryFn: async (): Promise<IImportData> => {
      setImportStatus(null);
      setErrors(null);
      const importData = await fetchImportStatus(importMutation.data!.importId);

      if (importData.status === ExportImportStatus.SUCCEEDED) {
        dynamicNotification(
          __("Importu danych został zakończony pomyślnie."),
          "success"
        );
      } else if (importData.status === ExportImportStatus.FAILED) {
        dynamicNotification(
          __("Importu danych zakończony niepowodzeniem."),
          "error",
          "top-right",
          7000
        );
        setErrors({
          errors: importData.errors,
          rowErrors: importData.rowErrors,
        });
      }

      setImportStatus(importData.status);
      return importData;
    },
  });

  return {
    importMutation,
    isStatusFetched: importStatusQuery.isFetched,
    importStatus,
    importErrors: errors,
  };
};

export default ImportAbsenceDataQueries;
